/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { di } from 'react-magnetic-di';

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';

import {
	PageContextType,
	usePageContextType,
} from '../../../../common/ui/chat-input-action-buttons/current-page-as-context';
import type { InlineChatTrace } from '../../../../controllers/inline-chat-controller';

type ThinkingViewProps = {
	latestTrace: InlineChatTrace | null;
	onCancel: (options?: { skipWarning?: boolean }) => void;
	onFollow?: () => void;
	useShortMessages?: boolean;
	showProgressBar?: boolean;
	isComplete?: boolean;
	onCompleteAnimationFinished?: () => void;
};

export const ThinkingView = ({
	latestTrace,
	onCancel,
	onFollow,
	useShortMessages = false,
	showProgressBar = false,
	isComplete,
	onCompleteAnimationFinished,
}: ThinkingViewProps): React.JSX.Element => {
	di(usePageContextType);
	const pageContextType = usePageContextType();
	const isNotWhiteboardContext = pageContextType !== PageContextType.CONFLUENCE_WHITEBOARD;

	const useRotatingTitle =
		fg('cc-maui-phase-2') && expValEquals('cc-maui-experiment', 'isEnabled', true);

	return (
		<RovoThinkingBar
			status={latestTraceToStatus(latestTrace)}
			onStop={() => onCancel({ skipWarning: true })}
			onFollow={onFollow}
			showEscapeHint={isNotWhiteboardContext}
			useShortMessages={useShortMessages}
			showProgressBar={showProgressBar}
			isComplete={isComplete}
			onCompleteAnimationFinished={onCompleteAnimationFinished}
			useRotatingTitle={useRotatingTitle}
		/>
	);
};

const latestTraceToStatus = (latestTrace: InlineChatTrace | null): 'analyzing' | 'editing' => {
	if (
		latestTrace?.message_template === 'ACTION_CLASSIFICATION' &&
		(latestTrace?.action_key === 'aifc-adf-update' ||
			latestTrace?.action_key === 'aifc-confluence-whiteboard-update')
	) {
		return 'editing';
	}
	return 'analyzing';
};
